import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  DollarSign, TrendingUp, TrendingDown, Target, Calendar,
  Camera, Upload, Loader2, X, ChevronDown, ChevronUp,
  Wallet, Receipt, Store, Clock, Trash2, ZoomIn, Settings, Check,
  Sparkles, ShoppingCart, List, Pencil
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { useListContext } from "../contexts/ListContext";
import { useAuth } from "../contexts/AuthContext";
import { useGuest } from "../contexts/GuestContext";
import { GUEST_BUDGET_KEY, guestHasAnyAlert, syncGuestAlertProfile } from "../lib/guestAlerts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Budget Setup Modal ──────────────────────────────────────────────────

const BudgetSetupModal = ({ currentBudget, onSave, onClose }) => {
  const [monthly, setMonthly] = useState(currentBudget || "");

  const handleSave = () => {
    const val = parseFloat(monthly);
    if (isNaN(val) || val <= 0) return;
    onSave(val);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="budget-setup-overlay">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl" onClick={e => e.stopPropagation()} data-testid="budget-setup-modal">
        <div className="p-5 space-y-4">
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <Target className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="font-bold text-lg text-gray-900">Set Monthly Budget</h3>
            <p className="text-sm text-gray-500 mt-1">How much do you want to spend on groceries each month?</p>
          </div>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="number"
              placeholder="e.g. 600"
              value={monthly}
              onChange={e => setMonthly(e.target.value)}
              className="pl-9 text-lg font-semibold"
              data-testid="budget-input"
              autoFocus
            />
          </div>
          {monthly && parseFloat(monthly) > 0 && (
            <div className="flex gap-2 text-center">
              <div className="flex-1 bg-gray-50 rounded-lg p-2">
                <p className="text-xs text-gray-500">Weekly</p>
                <p className="text-sm font-bold text-gray-800">${(parseFloat(monthly) / 4.33).toFixed(0)}</p>
              </div>
              <div className="flex-1 bg-gray-50 rounded-lg p-2">
                <p className="text-xs text-gray-500">Daily</p>
                <p className="text-sm font-bold text-gray-800">${(parseFloat(monthly) / 30).toFixed(0)}</p>
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button
              className="flex-1 bg-green-500 hover:bg-green-600 text-white"
              onClick={handleSave}
              disabled={!monthly || parseFloat(monthly) <= 0}
              data-testid="save-budget-btn"
            >
              <Check className="h-4 w-4 mr-1" /> Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Receipt Detail Modal (with correction support) ──────────────────────

const ReceiptDetailModal = ({ receipt, onClose, onDelete, onUpdate }) => {
  const [editing, setEditing] = useState(null); // {field, index, value}
  const [saving, setSaving] = useState(false);
  const [lightbox, setLightbox] = useState(false);

  if (!receipt) return null;

  const saveCorrection = async (field, itemIndex, original, corrected) => {
    if (corrected === original || !corrected.trim()) { setEditing(null); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/budget/receipts/${receipt.id}/correct`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ field, item_index: itemIndex, original, corrected: corrected.trim() }),
      });
      if (res.ok) {
        // Update local receipt data
        if (field === "store_name") receipt.store_name = corrected.trim();
        else if (field === "total") receipt.total = parseFloat(corrected) || receipt.total;
        else if (field === "date") receipt.date = corrected.trim();
        else if (field === "item" && itemIndex != null) receipt.items[itemIndex].name = corrected.trim();
        else if (field === "item_price" && itemIndex != null) receipt.items[itemIndex].price = parseFloat(corrected) || 0;
        onUpdate?.();
      }
    } catch { /* ignore */ }
    setSaving(false);
    setEditing(null);
  };

  const EditableField = ({ value, field, index, className, prefix }) => {
    const isEditing = editing?.field === field && editing?.index === index;
    if (isEditing) {
      return (
        <input
          autoFocus
          defaultValue={editing.value}
          className={`bg-white border-2 border-violet-400 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 w-full ${className || ""}`}
          onBlur={(e) => saveCorrection(field, index, value, e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveCorrection(field, index, value, e.target.value); } if (e.key === "Escape") setEditing(null); }}
          data-testid={`edit-${field}-${index ?? "main"}`}
        />
      );
    }
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setEditing({ field, index, value: String(value || "") }); }}
        className={`text-left cursor-pointer active:bg-violet-100 hover:bg-violet-50 rounded-md px-1 py-0.5 -mx-1 transition-colors min-h-[28px] ${className || ""}`}
        title="Tap to correct"
        data-testid={`editable-${field}-${index ?? "main"}`}
      >
        {prefix}{value}
      </button>
    );
  };

  return (
    <>
    {/* Lightbox - rendered OUTSIDE modal for full-screen coverage */}
    {lightbox && receipt.image_url && (
      <div
        className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4"
        onClick={() => setLightbox(false)}
        data-testid="receipt-lightbox"
      >
        <button
          onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
          className="absolute top-4 right-4 bg-white/20 hover:bg-white/40 text-white rounded-full p-3 transition-colors z-10"
        >
          <X className="h-6 w-6" />
        </button>
        <img
          src={receipt.image_url}
          alt="Receipt enlarged"
          className="max-w-full max-h-[90vh] object-contain rounded-lg"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    )}

    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="receipt-detail-overlay">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()} data-testid="receipt-detail-modal">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 text-sm">
              <EditableField value={receipt.store_name || "Receipt"} field="store_name" className="text-sm font-bold" />
            </h3>
            <p className="text-xs text-gray-500">
              <EditableField value={receipt.date || new Date(receipt.parsed_at).toLocaleDateString()} field="date" className="text-xs" />
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full" data-testid="close-receipt-detail">
            <X className="h-4 w-4 text-gray-400" />
          </button>
        </div>

        {receipt.image_url && (
          <div className="bg-gray-50 p-4 flex justify-center">
            <button
              onClick={() => setLightbox(true)}
              className="relative group"
              data-testid="receipt-image-zoom"
            >
              <img
                src={receipt.image_url}
                alt="Receipt"
                className="max-h-56 object-contain rounded-lg group-hover:opacity-80 transition-opacity"
              />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="bg-black/60 text-white rounded-full p-2">
                  <ZoomIn className="h-5 w-5" />
                </div>
              </div>
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-2.5 py-1 rounded-full">
                Tap to enlarge
              </div>
            </button>
          </div>
        )}

        <div className="p-4 space-y-3">
          {receipt.total != null && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
              <p className="text-xs text-green-600 font-medium">Total</p>
              <p className="text-2xl font-bold text-green-700">
                <EditableField value={receipt.total.toFixed(2)} field="total" prefix="$" className="text-2xl font-bold" />
              </p>
            </div>
          )}

          {receipt.savings > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-2 text-center">
              <p className="text-xs text-orange-600">You Saved <span className="font-bold">${receipt.savings.toFixed(2)}</span></p>
            </div>
          )}

          {receipt.items && receipt.items.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {receipt.items.length} Items Detected
                </p>
                <p className="text-xs text-violet-500 font-medium flex items-center gap-1">
                  <Pencil className="h-3 w-3" /> Tap any item to edit
                </p>
              </div>
              <div className="space-y-1">
                {receipt.items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-2 px-2.5 bg-gray-50 rounded-lg hover:bg-violet-50/50 transition-colors" data-testid={`receipt-item-row-${i}`}>
                    <div className="flex-1 min-w-0 mr-2">
                      <EditableField value={item.name} field="item" index={i} className="text-sm text-gray-800 block" />
                      {item.quantity > 1 && <span className="text-xs text-gray-400 ml-1">x{item.quantity}</span>}
                      {item.discount > 0 && <span className="text-xs text-orange-500 ml-1">-${item.discount.toFixed(2)}</span>}
                    </div>
                    <EditableField value={(item.price || 0).toFixed(2)} field="item_price" index={i} prefix="$" className="text-sm font-semibold text-gray-700 flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {!receipt.parse_success && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
              <p className="text-xs text-amber-700 font-medium">AI couldn't fully read this receipt</p>
              <p className="text-xs text-amber-600">Tap the receipt image above to enlarge it and compare. You can add items manually below or re-upload a clearer photo.</p>
              {receipt.image_url && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-amber-700 border-amber-300 hover:bg-amber-100 text-xs"
                  onClick={() => setLightbox(true)}
                  data-testid="enlarge-receipt-btn"
                >
                  <ZoomIn className="h-3 w-3 mr-1" /> Enlarge Receipt Photo
                </Button>
              )}
            </div>
          )}

          {saving && (
            <div className="flex items-center justify-center gap-2 text-xs text-violet-500">
              <Loader2 className="h-3 w-3 animate-spin" /> Saving correction...
            </div>
          )}

          <Button
            variant="outline"
            className="w-full text-red-500 border-red-200 hover:bg-red-50"
            onClick={() => { onDelete(receipt.id); onClose(); }}
            data-testid="delete-receipt-btn"
          >
            <Trash2 className="h-4 w-4 mr-1" /> Delete Receipt
          </Button>
        </div>
      </div>
    </div>
    </>
  );
};

// ── Shared price helper (matches Home tab logic) ────────────────────────

const getItemPrice = (item, marketPrices) => {
  if (item.deal_selected && item.estimated_price != null) return item.estimated_price;
  const mp = marketPrices[item.name];
  if (mp) return mp.avg_price;
  return item.estimated_price || 2.99;
};

// ── List Budget Card ────────────────────────────────────────────────────

const ListBudgetCard = ({ list, onUpdateBudget, onToggleInclude, marketPrices }) => {
  const [editingBudget, setEditingBudget] = useState(false);
  const [budgetVal, setBudgetVal] = useState(list.budget || "");

  const items = list.items || [];
  const estimated = items.reduce((s, i) => {
    const price = getItemPrice(i, marketPrices);
    return s + price * (i.quantity || 1);
  }, 0);
  const listBudget = list.budget || 0;
  const pct = listBudget > 0 ? Math.min((estimated / listBudget) * 100, 100) : 0;
  const isOver = estimated > listBudget && listBudget > 0;
  const isIncluded = list.budgetType === "monthly";

  const saveBudget = () => {
    const val = parseFloat(budgetVal);
    if (!isNaN(val) && val >= 0) {
      onUpdateBudget(list.id, val);
    }
    setEditingBudget(false);
  };

  const colorMap = {
    green: "bg-green-400", blue: "bg-blue-400", purple: "bg-purple-400",
    orange: "bg-orange-400", red: "bg-red-400", pink: "bg-pink-400",
    yellow: "bg-yellow-400", teal: "bg-teal-400",
  };
  const dotColor = colorMap[list.color] || "bg-green-400";

  return (
    <div className="bg-gray-50 rounded-xl p-3 space-y-2" data-testid={`list-budget-${list.id}`}>
      {/* List header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dotColor}`} />
          <span className="text-sm font-semibold text-gray-800 truncate">{list.name}</span>
          <span className="text-xs text-gray-400 flex-shrink-0">{items.length} item{items.length !== 1 ? "s" : ""}</span>
        </div>
        <span className="text-base font-bold text-gray-900" data-testid={`list-estimate-${list.id}`}>
          ${estimated.toFixed(2)}
        </span>
      </div>

      {/* Budget row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500">List budget:</span>
          {editingBudget ? (
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-400">$</span>
              <input
                type="number"
                className="w-16 text-xs px-1.5 py-0.5 border border-gray-300 rounded focus:ring-1 focus:ring-green-400 outline-none"
                value={budgetVal}
                onChange={e => setBudgetVal(e.target.value)}
                onKeyDown={e => e.key === "Enter" && saveBudget()}
                onBlur={saveBudget}
                autoFocus
                data-testid={`list-budget-input-${list.id}`}
              />
            </div>
          ) : (
            <button
              className="flex items-center gap-1 text-xs font-medium text-gray-700 hover:text-green-600 transition-colors"
              onClick={() => { setBudgetVal(list.budget || ""); setEditingBudget(true); }}
              data-testid={`edit-list-budget-${list.id}`}
            >
              ${listBudget.toFixed(0)}
              <Pencil className="h-2.5 w-2.5 text-gray-400" />
            </button>
          )}
        </div>
        {listBudget > 0 && (
          <span className={`text-xs font-medium ${isOver ? "text-red-500" : "text-gray-500"}`}>
            {isOver ? "Over by $" + (estimated - listBudget).toFixed(2) : "$" + (listBudget - estimated).toFixed(2) + " left"}
          </span>
        )}
      </div>

      {/* Progress bar */}
      {listBudget > 0 && (
        <Progress
          value={pct}
          className={`h-1.5 ${isOver ? "[&>div]:bg-red-400" : "[&>div]:bg-green-400"}`}
        />
      )}

      {/* Include in monthly toggle */}
      <div className="flex items-center justify-between pt-1 border-t border-gray-200/60">
        <span className="text-xs text-gray-500">Include in Monthly Budget</span>
        <Switch
          checked={isIncluded}
          onCheckedChange={() => onToggleInclude(list.id)}
          className="data-[state=checked]:bg-green-500"
          data-testid={`toggle-include-${list.id}`}
        />
      </div>
    </div>
  );
};

// ── AI Insights Section ─────────────────────────────────────────────────

const AIInsightsSection = ({ lists }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const groceryLists = lists.map(l => ({
        name: l.name,
        item_count: (l.items || []).filter(i => !i.completed).length,
        estimated_total: (l.items || []).filter(i => !i.completed)
          .reduce((s, i) => s + (i.estimated_price || 2.99) * (i.quantity || 1), 0),
        budget: l.budget || 0,
        included_in_monthly: l.budgetType === "monthly",
      }));

      const res = await fetch(`${BACKEND_URL}/api/budget/insights`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          grocery_lists: groceryLists,
          guest_budget: parseFloat(localStorage.getItem(GUEST_BUDGET_KEY) || "0") || 0,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setInsights(data.insights || []);
      }
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  const typeStyles = {
    saving: { bg: "bg-green-50", border: "border-green-200", icon: "text-green-600", label: "Saving" },
    warning: { bg: "bg-red-50", border: "border-red-200", icon: "text-red-600", label: "Alert" },
    tip: { bg: "bg-blue-50", border: "border-blue-200", icon: "text-blue-600", label: "Tip" },
    trend: { bg: "bg-purple-50", border: "border-purple-200", icon: "text-purple-600", label: "Trend" },
  };

  return (
    <Card data-testid="ai-insights-section">
      <CardContent className="p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-500" />
            <span className="text-sm font-semibold text-gray-700">AI Budget Insights</span>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={fetchInsights}
            disabled={loading}
            className="text-xs h-7"
            data-testid="get-insights-btn"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
            {loading ? "Analyzing..." : insights ? "Refresh" : "Get Insights"}
          </Button>
        </div>

        {!insights && !loading && (
          <p className="text-xs text-gray-400 text-center py-2">
            Tap "Get Insights" for AI-powered spending analysis based on your receipts and grocery lists
          </p>
        )}

        {insights && (
          <div className="space-y-2">
            {insights.map((insight, i) => {
              const style = typeStyles[insight.type] || typeStyles.tip;
              return (
                <div key={i} className={`${style.bg} border ${style.border} rounded-lg p-2.5`} data-testid={`insight-${i}`}>
                  <div className="flex items-start gap-2">
                    <Badge className={`${style.bg} ${style.icon} text-xs px-1.5 py-0 border-0 flex-shrink-0 mt-0.5`}>
                      {style.label}
                    </Badge>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{insight.title}</p>
                      <p className="text-xs text-gray-600 mt-0.5">{insight.body}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Main Budget Component ───────────────────────────────────────────────

const Budget = () => {
  const { lists, handleUpdateList } = useListContext();
  const { user } = useAuth();
  const { isGuest, promptSignup } = useGuest();
  const [summary, setSummary] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewMode, setViewMode] = useState("monthly");
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showReceipts, setShowReceipts] = useState(false);
  const [showLists, setShowLists] = useState(true);
  const fileInputRef = useRef(null);
  const [marketPrices, setMarketPrices] = useState({});

  const loadData = async () => {
    setLoading(true);
    // Read at call time so a freshly saved guest budget is used (avoid stale closure)
    const userParam = isGuest
      ? `?guest_budget=${parseFloat(localStorage.getItem(GUEST_BUDGET_KEY) || "0") || 0}`
      : "";
    try {
      const [sumRes, recRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/budget/summary${userParam}`),
        fetch(`${BACKEND_URL}/api/budget/receipts${userParam}`),
      ]);
      if (sumRes.ok) setSummary(await sumRes.json());
      if (recRes.ok) {
        const recData = await recRes.json();
        setReceipts(recData.receipts || []);
      }
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  // Fetch market prices for all list items (same API as Home tab)
  const fetchMarketPrices = useCallback(async () => {
    const allItems = lists.flatMap(l => (l.items || []).map(i => i.name));
    if (allItems.length === 0) return;
    try {
      const itemStr = encodeURIComponent([...new Set(allItems)].join(","));
      const res = await fetch(`${BACKEND_URL}/api/weekly-ads/price-intelligence?items=${itemStr}`);
      if (res.ok) {
        const data = await res.json();
        setMarketPrices(data.prices || {});
      }
    } catch (e) { /* ignore */ }
  }, [lists]);

  useEffect(() => { loadData(); }, []);
  useEffect(() => { fetchMarketPrices(); }, [fetchMarketPrices]);

  // Per-list estimate helper (uses market prices like Home tab)
  const getListEstimate = (list) => {
    return (list.items || [])
      .reduce((s, i) => s + getItemPrice(i, marketPrices) * (i.quantity || 1), 0);
  };

  // Total estimate from lists included in monthly budget
  const includedEstimate = lists
    .filter(l => l.budgetType === "monthly")
    .reduce((sum, l) => sum + getListEstimate(l), 0);

  const separateEstimate = lists
    .filter(l => l.budgetType !== "monthly")
    .reduce((sum, l) => sum + getListEstimate(l), 0);

  const allListsEstimate = lists.reduce((sum, l) => sum + getListEstimate(l), 0);

  const handleSaveBudget = async (monthly) => {
    if (isGuest) {
      localStorage.setItem(GUEST_BUDGET_KEY, String(monthly));
      if (guestHasAnyAlert()) syncGuestAlertProfile().catch(() => {});
      setShowSetup(false);
      loadData();
      return;
    }
    try {
      await fetch(`${BACKEND_URL}/api/budget/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ monthly_budget: monthly }),
      });
      setShowSetup(false);
      loadData();
    } catch (e) { /* ignore */ }
  };

  const handleUploadReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const params = user?.id ? `?user_id=${user.id}` : "";
      const res = await fetch(`${BACKEND_URL}/api/budget/receipts/upload${params}`, {
        method: "POST",
        body: formData,
      });
      if (res.ok) loadData();
    } catch (e) { /* ignore */ }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDeleteReceipt = async (id) => {
    try {
      await fetch(`${BACKEND_URL}/api/budget/receipts/${id}`, { method: "DELETE" });
      loadData();
    } catch (e) { /* ignore */ }
  };

  const handleUpdateListBudget = (listId, newBudget) => {
    handleUpdateList(listId, { budget: newBudget });
  };

  const handleToggleInclude = (listId) => {
    const list = lists.find(l => l.id === listId);
    if (!list) return;
    const newType = list.budgetType === "monthly" ? "separate" : "monthly";
    handleUpdateList(listId, { budgetType: newType });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 text-green-500 animate-spin" />
      </div>
    );
  }

  const isConfigured = summary?.is_configured;
  const monthlyBudget = summary?.monthly_budget || 0;
  const weeklyBudget = summary?.weekly_budget || 0;
  const dailyBudget = summary?.daily_budget || 0;
  const totalSpent = summary?.total_spent || 0;
  const remaining = summary?.remaining || 0;
  const daysLeft = summary?.days_left || 0;
  const dailyRemaining = summary?.daily_remaining || 0;

  const displayBudget = viewMode === "monthly" ? monthlyBudget : viewMode === "weekly" ? weeklyBudget : dailyBudget;
  const displaySpent = viewMode === "monthly" ? totalSpent : viewMode === "weekly" ? totalSpent / 4.33 : totalSpent / 30;
  const displayRemaining = displayBudget - displaySpent;
  const spentPct = displayBudget > 0 ? Math.min((displaySpent / displayBudget) * 100, 100) : 0;
  const isOver = displaySpent > displayBudget && displayBudget > 0;

  const storeSpending = summary?.store_spending || {};
  const topStores = Object.entries(storeSpending)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  // Projected total = spent + included list estimates
  const projectedTotal = totalSpent + includedEstimate;
  const projectedOver = monthlyBudget > 0 && projectedTotal > monthlyBudget;

  return (
    <div className="p-4 space-y-4 pb-24" data-testid="budget-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Budget Tracker</h2>
          <p className="text-sm text-gray-500">Track your grocery spending</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowSetup(true)}
          className="text-gray-600"
          data-testid="edit-budget-btn"
        >
          <Settings className="h-3.5 w-3.5 mr-1" />
          {isConfigured ? "Edit" : "Set Budget"}
        </Button>
      </div>

      {/* Budget not configured */}
      {!isConfigured && (
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-6 text-center">
            <Target className="h-10 w-10 text-green-400 mx-auto mb-3" />
            <h3 className="font-bold text-gray-900 mb-1">Set Your Monthly Budget</h3>
            <p className="text-sm text-gray-500 mb-4">Start tracking your grocery spending against a budget goal</p>
            <Button
              className="bg-green-500 hover:bg-green-600 text-white"
              onClick={() => setShowSetup(true)}
              data-testid="setup-budget-cta"
            >
              <Target className="h-4 w-4 mr-2" /> Set Budget
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Budget Overview */}
      {isConfigured && (
        <>
          {/* View Mode Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            {["monthly", "weekly", "daily"].map(mode => (
              <button
                key={mode}
                className={`flex-1 text-xs font-medium py-1.5 rounded-md capitalize transition-colors ${
                  viewMode === mode ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
                }`}
                onClick={() => setViewMode(mode)}
                data-testid={`view-${mode}`}
              >
                {mode}
              </button>
            ))}
          </div>

          {/* Main Budget Card */}
          <Card className={isOver ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}>
            <CardContent className="p-4">
              <div className="text-center mb-3">
                <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">{viewMode} Spending</p>
                <h3 className="text-3xl font-bold text-gray-900 mt-1">${displaySpent.toFixed(2)}</h3>
                <p className="text-sm text-gray-600">of ${displayBudget.toFixed(2)} {viewMode} budget</p>
              </div>

              <Progress
                value={spentPct}
                className={`h-3 mb-3 ${isOver ? "[&>div]:bg-red-500" : "[&>div]:bg-green-500"}`}
              />

              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-xs text-gray-500">Remaining</p>
                  <p className={`text-sm font-bold ${displayRemaining >= 0 ? "text-green-600" : "text-red-600"}`}>
                    ${Math.abs(displayRemaining).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Days Left</p>
                  <p className="text-sm font-bold text-gray-800">{daysLeft}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Daily Pace</p>
                  <p className="text-sm font-bold text-gray-800">${dailyRemaining.toFixed(2)}/day</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Projected total (spending + included list estimates) */}
          {includedEstimate > 0 && monthlyBudget > 0 && (
            <div className={`rounded-lg text-xs ${projectedOver ? "bg-red-50 border border-red-200" : "bg-emerald-50 border border-emerald-200"}`} data-testid="projected-total">
              <div className="flex items-center justify-between px-3 pt-2 pb-1">
                <span className="text-gray-600">
                  <TrendingUp className="h-3 w-3 inline mr-1" />
                  Projected (spent + planned lists)
                </span>
                <span className={`font-bold ${projectedOver ? "text-red-600" : "text-emerald-600"}`}>
                  ${projectedTotal.toFixed(2)} / ${monthlyBudget.toFixed(2)}
                </span>
              </div>
              <div className="flex items-center justify-between px-3 pb-2 pt-0.5 border-t border-dashed border-gray-200/60">
                <span className="text-gray-500">
                  {projectedOver ? "Over budget by" : "Remaining after lists"}
                </span>
                <span className={`font-bold ${projectedOver ? "text-red-600" : "text-green-600"}`} data-testid="projected-remaining">
                  ${Math.abs(monthlyBudget - projectedTotal).toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Your Grocery Lists ─────────────────────────────────────── */}
      <div>
        <button
          className="flex items-center justify-between w-full py-2"
          onClick={() => setShowLists(!showLists)}
          data-testid="toggle-lists-section"
        >
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-4 w-4 text-green-600" />
            <span className="text-sm font-semibold text-gray-700">
              Your Grocery Lists ({lists.length})
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-gray-800">${allListsEstimate.toFixed(2)}</span>
            {showLists ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
          </div>
        </button>

        {showLists && (
          <div className="space-y-2 mt-1" data-testid="lists-budget-section">
            {lists.map(list => (
              <ListBudgetCard
                key={list.id}
                list={list}
                onUpdateBudget={handleUpdateListBudget}
                onToggleInclude={handleToggleInclude}
                marketPrices={marketPrices}
              />
            ))}

            {/* Summary footer */}
            <div className="flex flex-col gap-1 px-1 pt-1">
              {includedEstimate > 0 && (
                <div className="flex items-center justify-between text-xs text-gray-500" data-testid="included-total">
                  <span>Included in Monthly Budget</span>
                  <span className="font-semibold text-green-600">${includedEstimate.toFixed(2)}</span>
                </div>
              )}
              {separateEstimate > 0 && (
                <div className="flex items-center justify-between text-xs text-gray-500" data-testid="separate-total">
                  <span>Separate Estimates</span>
                  <span className="font-semibold text-amber-600">${separateEstimate.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── AI Budget Insights ─────────────────────────────────────── */}
      <AIInsightsSection lists={lists} />

      {/* Receipt Upload Section */}
      <Card className="border-dashed border-2 border-gray-200 hover:border-green-300 transition-colors">
        <CardContent className="p-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleUploadReceipt}
            className="hidden"
            data-testid="receipt-file-input"
          />
          <div className="text-center">
            {uploading ? (
              <div className="py-4">
                <Loader2 className="h-8 w-8 text-green-500 animate-spin mx-auto mb-2" />
                <p className="text-sm text-gray-600 font-medium">AI is reading your receipt...</p>
                <p className="text-xs text-gray-400 mt-1">Extracting items, prices, and store info</p>
              </div>
            ) : (
              <>
                <div className="flex justify-center gap-3 mb-3">
                  <Button
                    className="bg-green-500 hover:bg-green-600 text-white"
                    onClick={() => {
                      if (isGuest) {
                        promptSignup({
                          headline: "Sign up to scan receipts",
                          subhead: "Receipt scanning is part of your 14-day free All-Access trial. No card required.",
                        });
                        return;
                      }
                      fileInputRef.current?.click();
                    }}
                    data-testid="upload-receipt-btn"
                  >
                    <Camera className="h-4 w-4 mr-2" /> Scan Receipt
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (isGuest) {
                        promptSignup({
                          headline: "Sign up to upload receipts",
                          subhead: "Receipt uploads are part of your 14-day free All-Access trial. No card required.",
                        });
                        return;
                      }
                      if (fileInputRef.current) {
                        fileInputRef.current.removeAttribute("capture");
                        fileInputRef.current.click();
                        fileInputRef.current.setAttribute("capture", "environment");
                      }
                    }}
                    data-testid="upload-receipt-file-btn"
                  >
                    <Upload className="h-4 w-4 mr-2" /> Upload
                  </Button>
                </div>
                <p className="text-xs text-gray-400">
                  {isGuest
                    ? "Sign up to unlock receipt scanning and track real spending."
                    : "AI extracts items & prices to track real spending and savings"}
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Store Spending Breakdown */}
      {topStores.length > 0 && (
        <Card>
          <CardContent className="p-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              <Store className="h-3 w-3 inline mr-1" /> Spending by Store
            </p>
            <div className="space-y-2">
              {topStores.map(([store, amount], i) => {
                const maxAmount = topStores[0][1] || 1;
                const barWidth = Math.max(15, (amount / maxAmount) * 100);
                return (
                  <div key={store} className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 w-24 truncate flex-shrink-0 font-medium">{store}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                      <div
                        className={`h-full rounded-full flex items-center pl-2 ${i === 0 ? "bg-green-200" : "bg-gray-200"}`}
                        style={{ width: `${barWidth}%` }}
                      >
                        <span className="text-xs font-bold text-gray-700">${amount.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Receipts */}
      <div>
        <button
          className="flex items-center justify-between w-full py-2"
          onClick={() => setShowReceipts(!showReceipts)}
          data-testid="toggle-receipts"
        >
          <div className="flex items-center gap-2">
            <Receipt className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-700">
              Receipts ({receipts.length})
            </span>
          </div>
          {showReceipts ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </button>

        {showReceipts && (
          <div className="space-y-2 mt-1">
            {receipts.length === 0 ? (
              <Card className="bg-gray-50">
                <CardContent className="p-4 text-center">
                  <Receipt className="h-6 w-6 text-gray-300 mx-auto mb-1" />
                  <p className="text-sm text-gray-500">No receipts yet</p>
                  <p className="text-xs text-gray-400">Upload your first receipt to start tracking</p>
                </CardContent>
              </Card>
            ) : (
              receipts.map(r => (
                <Card
                  key={r.id}
                  className="cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setSelectedReceipt(r)}
                  data-testid={`receipt-${r.id}`}
                >
                  <CardContent className="p-3 flex items-center gap-3">
                    {r.image_url ? (
                      <img src={r.image_url} alt="" className="w-10 h-10 object-cover rounded-lg flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Receipt className="h-4 w-4 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800">{r.store_name || "Receipt"}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{r.date || new Date(r.parsed_at).toLocaleDateString()}</span>
                        <span>{r.item_count} item{r.item_count !== 1 ? "s" : ""}</span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {r.total != null ? (
                        <p className="text-sm font-bold text-gray-800">${r.total.toFixed(2)}</p>
                      ) : (
                        <p className="text-xs text-gray-400">No total</p>
                      )}
                      {r.parse_success ? (
                        <Badge className="bg-green-100 text-green-700 text-xs">Parsed</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700 text-xs">Partial</Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showSetup && (
        <BudgetSetupModal
          currentBudget={monthlyBudget}
          onSave={handleSaveBudget}
          onClose={() => setShowSetup(false)}
        />
      )}
      {selectedReceipt && (
        <ReceiptDetailModal
          receipt={selectedReceipt}
          onClose={() => setSelectedReceipt(null)}
          onDelete={handleDeleteReceipt}
          onUpdate={() => loadData()}
        />
      )}
    </div>
  );
};

export default Budget;
